import { DataTable, type DataTableColumn, Input } from "@design";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { BarChart3, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getUsageByUserInfiniteOptions, type UserSpendRow } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Text } from "@/components/Text";
import { formatCompact, formatNumber, formatUsd } from "@/i18n/format";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { AdminPage } from "../AdminPage";
import { DEFAULT_RANGE, type RangeDays, RangeSelect, rangeStart } from "./usageRange";

const PAGE_SIZE = 25;

function useColumns(): DataTableColumn<UserSpendRow>[] {
  const { t } = useTranslation("adminUsage");
  const numeric = (value: string) => (
    <Text as="span" variant="body-sm" tone="secondary" className="tabular-nums">
      {value}
    </Text>
  );
  return [
    {
      key: "user",
      header: t("byUser.columns.user"),
      mobile: "primary",
      render: (row) => (
        <div className="flex min-w-0 flex-col text-left">
          <Text as="span" variant="body-sm" className="truncate">
            {row.userName || row.userEmail}
          </Text>
          <Text as="span" variant="caption" tone="tertiary" className="truncate">
            {row.userEmail}
          </Text>
        </div>
      ),
    },
    {
      key: "requests",
      header: t("byUser.columns.requests"),
      align: "right",
      render: (row) => numeric(formatNumber(row.requests)),
    },
    {
      key: "tokens",
      header: t("byUser.columns.tokens"),
      align: "right",
      render: (row) => numeric(formatCompact(row.tokensInput + row.tokensOutput)),
    },
    { key: "cost", header: t("byUser.columns.cost"), align: "right", render: (row) => numeric(formatUsd(row.costUsd)) },
  ];
}

/** /admin/usage/users: per-person spend, searchable, loaded in pages. */
export function UsageByUserPage() {
  const { t } = useTranslation("adminUsage");
  const [range, setRange] = useState<RangeDays>(DEFAULT_RANGE);
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search.trim());
  const columns = useColumns();

  const spend = useInfiniteQuery({
    ...getUsageByUserInfiniteOptions({
      query: { start_date: rangeStart(range), search: term || undefined, limit: PAGE_SIZE },
    }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.data.hasMore ? pages.length * PAGE_SIZE : undefined),
    placeholderData: keepPreviousData,
  });
  const rows = spend.data?.pages.flatMap((page) => page.data.users ?? []) ?? [];
  const total = spend.data?.pages[0]?.data.totalCount;

  return (
    <AdminPage title={t("byUser.title")} subtitle={t("byUser.subtitle")}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="md:max-w-xs md:flex-1">
          <Input
            leadingIcon={<Search />}
            placeholder={t("byUser.searchPlaceholder")}
            aria-label={t("byUser.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <RangeSelect label={t("range.label")} value={range} onChange={setRange} />
        {total !== undefined && (
          <Text as="span" variant="body-sm" tone="tertiary" className="md:ml-auto">
            {t("byUser.total", { count: total })}
          </Text>
        )}
      </div>
      {spend.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("byUser.tableLabel")}
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row.userId)}
          loading={spend.isPending}
          empty={<EmptyState icon={<BarChart3 size={32} />} title={term ? t("byUser.noResults") : t("byUser.empty")} />}
          loadMore={{
            onLoadMore: () => void spend.fetchNextPage(),
            hasMore: spend.hasNextPage,
            loading: spend.isFetchingNextPage,
            label: t("byUser.loadMore"),
          }}
        />
      )}
    </AdminPage>
  );
}
